import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Dialog, DialogTitle, DialogContent, IconButton, Box, Typography, Stack, Fab, Tooltip,
} from '@mui/material';
import { Info, X, Sparkles, Users, Lightbulb } from 'lucide-react';
import { FEATURE_HELP } from '../../data/featureHelp';
import { useTranslation } from 'react-i18next';

/**
 * Global "what does this page do" affordance. Mounted once in Layout.tsx so every routed page
 * gets it automatically — content is looked up by the current URL against FEATURE_HELP rather
 * than each page wiring its own button. Renders nothing if the current route has no entry yet.
 */
export function LearnMoreButton() {
  const { t } = useTranslation('ui');
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const content = FEATURE_HELP[location.pathname];

  if (!content) return null;

  return (
    <>
      <Tooltip title={t('learnMoreAboutThisPage')} placement="left">
        <Fab
          size="small"
          onClick={() => setOpen(true)}
          sx={{
            position: 'fixed', bottom: 24, left: 24, zIndex: (theme) => theme.zIndex.drawer + 1,
            bgcolor: 'background.paper', color: 'primary.main',
            border: '1px solid', borderColor: 'divider',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <Info size={20} />
        </Fab>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: 'rgba(25, 118, 210, 0.1)', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Info size={17} />
            </Box>
            <Typography variant="subtitle1" fontWeight={700}>{content.title}</Typography>
          </Stack>
          <IconButton size="small" onClick={() => setOpen(false)}><X size={18} /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Stack spacing={2.5}>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              {content.summary}
            </Typography>

            <Box>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                <Sparkles size={14} color="#1976d2" />
                <Typography variant="overline" sx={{ fontWeight: 700, color: 'primary.main', letterSpacing: 0.5 }}>
                  {t('whatHappensHere')}
                </Typography>
              </Stack>
              <Stack spacing={1}>
                {content.whatHappens.map((item, i) => (
                  <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                    <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: 'primary.main', mt: 0.9, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{item}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>

            {content.whoItsFor && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Users size={14} style={{ opacity: 0.6, flexShrink: 0 }} />
                <Typography variant="caption" color="text.secondary">{content.whoItsFor}</Typography>
              </Stack>
            )}

            {content.tips && content.tips.length > 0 && (
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(237, 108, 2, 0.06)' }}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                  <Lightbulb size={14} color="#ed6c02" />
                  <Typography variant="caption" fontWeight={700} color="warning.dark">{t('goodToKnow')}</Typography>
                </Stack>
                <Stack spacing={0.5}>
                  {content.tips.map((tip, i) => (
                    <Typography key={i} variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                      {tip}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
